<template>
    <!-- Figma "Invoices for this Job" (5535:1856): breadcrumb bar, invoice table with ⋮ actions. Totals, Add New Invoice
         and the Pay / Edit / Share / Void actions are kept from before. -->
    <div
        class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[20px] pt-[35px] laptop:pt-[20px] border-gray-300 font-roboto">
        <!-- below laptop: the round back button on the card's top edge, back to the job (like the detail pages) -->
        <DetailBackButton v-if="is_from_mobile == 0" :fallback="'/job/detail/' + $route.params.id" />
        <div
            class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[20px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px]">
            <template v-if="!isSilentLogin">
                <router-link to="/work-order/service-and-repair" class="no-underline text-[#8479E1]">Service &amp;
                    Repair</router-link>
                <span class="text-[#CCCCCC]">|</span>
            </template>
            <template v-if="!isMobileApp">
                <router-link :to="'/job/detail/' + $route.params.id" class="no-underline text-[#8479E1]">Job
                    Details</router-link>
                <span class="text-[#CCCCCC]">|</span>
            </template>
            <span class="text-[#000000]">Invoices</span>
        </div>

        <div v-if="!loading" class="flex flex-col gap-[15px]">
            <div class="flex flex-wrap items-center justify-between gap-[10px]">
                <div class="flex flex-wrap items-center gap-[10px]">
                    <span
                        class="flex items-center gap-[8px] h-[36px] px-[14px] rounded-[8px] bg-[#F4F5FB] text-[13px] text-[#5B616B]">Job
                        Total <b class="text-[#2E3138]">${{ $helper.formatAmount(invoiceList.job_total) }}</b></span>
                    <span
                        class="flex items-center gap-[8px] h-[36px] px-[14px] rounded-[8px] bg-[#E7F7F0] text-[13px] text-[#1FA971]">Paid
                        <b>${{ $helper.formatAmount(invoiceList.paid) }}</b></span>
                    <span
                        class="flex items-center gap-[8px] h-[36px] px-[14px] rounded-[8px] bg-[#FDECEC] text-[13px] text-[#C43333]">Outstanding
                        <b>${{ $helper.formatAmount(invoiceList.outstanding) }}</b></span>
                </div>
                <button type="button"
                    class="flex items-center gap-[8px] h-[36px] pl-[5px] pr-[15px] rounded-full border-1 border-[#E9ECF1] bg-white text-[14px] text-[#333333]"
                    @click="addInvoiceModalClick()">
                    <span class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-[#14C38E]">
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M7 1.5v11M1.5 7h11" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
                        </svg>
                    </span>
                    Add New Invoice
                </button>
            </div>

            <template v-if="invoiceList.list.length">
                <!-- desktop: same table as the Work Order lists (appointmentTable.vue); cards below 1280 like the lists.
                     No table-responsive: its overflow clipped the ⋮ actions menu (the table fits from 1280) -->
                <div class="mobile:hidden desktop:block font-roboto px-2.5 py-1">
                    <table class="w-full border-separate border-spacing-y-1">
                        <thead>
                            <tr class="rounded-lg border border-white shadow-[0_0_10px_0_#00000026]">
                                <th v-for="(header, index) in invoiceHeaders" :key="header.title"
                                    class="p-2 font-roboto font-medium text-[12px] largeDesktop:text-[14px] !bg-[#BA90C6] text-white"
                                    :class="{ 'rounded-l-lg': index === 0, 'rounded-r-lg text-right': index === invoiceHeaders.length - 1, 'cursor-pointer': header.key }"
                                    @click="header.key && sortBy(header.key)">
                                    <!-- same sort icons as the Work Order lists (appointmentTable.vue) -->
                                    <span class="flex items-center gap-[6px]" :class="header.key ? 'justify-between' : 'justify-end'">
                                        {{ header.title }}
                                        <img v-if="header.key" class="w-4 h-4 object-contain"
                                            :src="sort.key !== header.key ? require('@/assets/images/sorting-icon.png') : (sort.dir === 'ASC' ? require('@/assets/images/asc.svg') : require('@/assets/images/desc.svg'))"
                                            :alt="sort.key === header.key ? 'Sorted ' + sort.dir : 'Sort'">
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white text-slate-800">
                            <tr v-for="(value, index) in pagedInvoices" :key="value.id || index"
                                class="rounded-lg shadow-[0_0_10px_0_#00000026] font-roboto text-[12px] largeDesktop:text-[14px] text-[#333333]">
                                <td class="p-2 bg-white rounded-l-lg">
                                    <!-- ID opens the invoice, same as ⋮ → View -->
                                    <router-link :to="invoiceRoute(value)" active-class=""
                                        class="no-underline text-[#7695FF] hover:underline">{{ value.id }}</router-link>
                                </td>
                                <td class="p-2 bg-white">{{ value.date ? $helper.formatDateTime(value.date) : '-' }}</td>
                                <td class="p-2 bg-white">{{ value.payment_type || '-' }}</td>
                                <td class="p-2 bg-white">${{ $helper.formatAmount(value.amount) }}</td>
                                <td class="p-2 bg-white">{{ value.paid_date ? $helper.formattedDate(value.paid_date) : '-' }}</td>
                                <td class="p-2 bg-white">
                                    <span class="text-white rounded-md p-[5px] py-[2px] largeDesktop:px-2.5 largeDesktop:py-1 capitalize block w-fit"
                                        :style="{ backgroundColor: statusColor(value.status) }">{{ value.status || '-' }}</span>
                                </td>
                                <td class="p-2 bg-white rounded-r-lg">
                                    <span class="flex justify-end">
                                    <div class="dropdown">
                                        <button
                                            class="w-[30px] h-[30px] rounded-full flex items-center justify-center border-1 border-[#E9ECF1] bg-white"
                                            type="button" data-bs-toggle="dropdown" aria-expanded="false"
                                            aria-label="Invoice actions">
                                            <svg width="4" height="14" viewBox="0 0 4 14" aria-hidden="true">
                                                <circle cx="2" cy="2" r="1.6" fill="#5B616B" />
                                                <circle cx="2" cy="7" r="1.6" fill="#5B616B" />
                                                <circle cx="2" cy="12" r="1.6" fill="#5B616B" />
                                            </svg>
                                        </button>
                                        <ul
                                            class="dropdown-menu dropdown-menu-end bg-[#fff] border-0 !p-[6px] rounded-[8px] shadow-[0px_0px_10px_0px_#00000026]">
                                            <li v-if="value?.status == 'Unpaid'"><button
                                                    class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                    @click="payNowClicked(value)">Pay Now</button></li>
                                            <li v-if="value?.status == 'Unpaid'"><button
                                                    class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                    @click="editClicked(value)">Edit</button></li>
                                            <li><button
                                                    class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                    @click="downloadClicked(value)">View</button></li>
                                            <li v-if="value?.status == 'Unpaid'"><button
                                                    class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                    @click="shareClicked(value)">Share</button></li>
                                            <li v-if="value?.status == 'Unpaid'"><button
                                                    class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                    @click="voidClicked(value)">Void</button></li>
                                        </ul>
                                    </div>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <!-- mobile cards -->
                <div class="desktop:hidden flex flex-col gap-[12px]">
                    <div v-for="(value, index) in pagedInvoices" :key="'m-' + (value.id || index)"
                        class="flex flex-col gap-[8px] rounded-[10px] px-[15px] py-[12px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px]">
                        <div class="flex items-center justify-between gap-2">
                            <router-link :to="invoiceRoute(value)" active-class=""
                                class="no-underline text-[#6C79E0] font-medium hover:underline">{{ value.id }}</router-link>
                            <span class="flex items-center gap-[8px]"><span
                                    class="text-white rounded-md px-2 py-[2px] capitalize block w-fit text-[12px] leading-[16px]"
                                    :style="{ backgroundColor: statusColor(value.status) }">{{ value.status || '-' }}</span>
                                <div class="dropdown">
                                    <button
                                        class="w-[30px] h-[30px] rounded-full flex items-center justify-center border-1 border-[#E9ECF1] bg-white"
                                        type="button" data-bs-toggle="dropdown" aria-expanded="false"
                                        aria-label="Invoice actions">
                                        <svg width="4" height="14" viewBox="0 0 4 14" aria-hidden="true">
                                            <circle cx="2" cy="2" r="1.6" fill="#5B616B" />
                                            <circle cx="2" cy="7" r="1.6" fill="#5B616B" />
                                            <circle cx="2" cy="12" r="1.6" fill="#5B616B" />
                                        </svg>
                                    </button>
                                    <ul
                                        class="dropdown-menu dropdown-menu-end bg-[#fff] border-0 !p-[6px] rounded-[8px] shadow-[0px_0px_10px_0px_#00000026]">
                                        <li v-if="value?.status == 'Unpaid'"><button
                                                class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                @click="payNowClicked(value)">Pay Now</button></li>
                                        <li v-if="value?.status == 'Unpaid'"><button
                                                class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                @click="editClicked(value)">Edit</button></li>
                                        <li><button
                                                class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                @click="downloadClicked(value)">View</button></li>
                                        <li v-if="value?.status == 'Unpaid'"><button
                                                class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                @click="shareClicked(value)">Share</button></li>
                                        <li v-if="value?.status == 'Unpaid'"><button
                                                class="dropdown-item !rounded-[5px] text-[13px] hover:!text-[#fff] hover:!bg-[#7695FF]"
                                                @click="voidClicked(value)">Void</button></li>
                                    </ul>
                                </div>
                            </span>
                        </div>
                        <div class="grid grid-cols-2 gap-[6px] text-[#333333]">
                            <span><span class="block text-[12px] text-[#999999]">Issued</span>{{ value.date ?
                                $helper.formatDateTime(value.date) : '-' }}</span>
                            <span><span class="block text-[12px] text-[#999999]">Amount</span>${{
                                $helper.formatAmount(value.amount) }}</span>
                            <span><span class="block text-[12px] text-[#999999]">Payment Type</span>{{
                                value.payment_type || '-' }}</span>
                            <span><span class="block text-[12px] text-[#999999]">Paid Date</span>{{ value.paid_date ?
                                $helper.formattedDate(value.paid_date) : '-' }}</span>
                        </div>
                    </div>
                </div>
                <!-- invoice/list has no paging: paginate client-side with the Work Order listings' pager -->
                <div v-if="invoiceList.list.length > limit"
                    class="flex mobile:flex-col tablet:flex-row mobile:gap-3 tablet:gap-0 items-center justify-between">
                    <paginationEntries class="mobile:hidden tablet:block" :current-page="page" :per-page="limit"
                        :total-items="invoiceList.list.length" />
                    <customPagination :page="page" :total_pages="totalPages" :update-page="updatePage" />
                </div>
            </template>
            <div v-else
                class="flex items-center justify-center min-h-[150px] rounded-[10px] border-1 border-dashed border-[#CCCCCC] bg-[#EFEFEF80]">
                <p class="m-0 text-[14px] text-[#666666]">No Invoice Available</p>
            </div>
        </div>
    </div>

    <addInvoiceModal ref="addInvoiceModal" @success="handleInvoiceSuccess" />
    <viewInvoiceDetailModal ref="viewInvoiceDetailModal" :invoiceDetail="selectedInvoice" />
    <shareInvoiceModal ref="shareInvoiceModal" :invoiceDetail="selectedInvoice" />
    <voidModal ref="voidModal" @success="updateInvoiceList" />
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { isSilentLogin, isFromMobile } from "@/helpers/embedMode";
import addInvoiceModal from "../../components/invoice/addInvoiceModal.vue";
import viewInvoiceDetailModal from "../../components/invoice/viewInvoiceDetailModal.vue";
import shareInvoiceModal from "../../components/invoice/shareInvoiceModal.vue";
import voidModal from "../../components/invoice/voidModal.vue";
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import customPagination from "@/components/partialComponent/customPagination.vue";
import paginationEntries from "@/components/partialComponent/pagination-entries.vue";

export default {
    data() {
        return {
            invoiceHeaders: [
                { title: "Invoice", key: "id" }, { title: "Issued", key: "date" }, { title: "Payment Type", key: "payment_type" },
                { title: "Amount", key: "amount" }, { title: "Paid Date", key: "paid_date" }, { title: "Status", key: "status" },
                { title: "Action" },
            ],
            // client-side sort (invoice/list has no sort or paging); first click DESC like the lists
            sort: { key: "", dir: "" },
            loading: true,
            invoiceItemList: [],
            selectedInvoice: null,
            page: 1,
            limit: 10,
            is_from_mobile: localStorage.getItem("is_from_mobile", 0) ?? 0
        }
    },

    components: {
        addInvoiceModal,
        viewInvoiceDetailModal,
        shareInvoiceModal,
        voidModal,
        DetailBackButton,
        customPagination,
        paginationEntries,
    },

    computed: {
        isSilentLogin() {
            return isSilentLogin();
        },
        // the mobile app webview has its own back navigation: no Job Details crumb
        isMobileApp() {
            return isFromMobile(this.$route);
        },
        ...mapGetters(["invoiceList"]),
        totalPages() {
            return Math.ceil((this.invoiceList?.list?.length || 0) / this.limit);
        },
        sortedInvoices() {
            const list = [...(this.invoiceList?.list || [])];
            const { key, dir } = this.sort;
            if (!key) return list;
            // numbers (id, amount, epoch dates) as numbers, date columns as dates, the rest as text; blanks last
            const isDate = key === "date" || key === "paid_date";
            const val = (v) => {
                if (v === null || v === undefined || v === "") return null;
                if (!isNaN(v)) return Number(v);
                const t = isDate ? Date.parse(v) : NaN;
                return isNaN(t) ? String(v).toLowerCase() : t;
            };
            return list.sort((a, b) => {
                const x = val(a[key]), y = val(b[key]);
                if (x === null || y === null) return x === y ? 0 : (x === null ? 1 : -1);
                const c = x < y ? -1 : (x > y ? 1 : 0);
                return dir === "ASC" ? c : -c;
            });
        },
        pagedInvoices() {
            const start = (this.page - 1) * this.limit;
            return this.sortedInvoices.slice(start, start + this.limit);
        },
    },

    watch: {
        invoiceList: function () {
            this.loading = false
            if (this.page > Math.max(this.totalPages, 1)) this.page = 1
        },
    },

    mounted() {
        this.getInvoiceList({
            job_type: 'sar',
            job_id: this.$route.params.id,
        })
    },

    methods: {
        ...mapActions(["getInvoiceList"]),
        sortBy(key) {
            this.sort = { key, dir: this.sort.key === key && this.sort.dir === "DESC" ? "ASC" : "DESC" };
            this.page = 1;
        },
        // solid status pill like the Work Order lists
        statusColor(status) {
            return { PAID: '#14A44D', UNPAID: '#F97316', VOID: '#686D76', VOIDED: '#686D76' }[String(status || '').toUpperCase()] || '#6B7280';
        },

        addInvoiceModalClick() {
            this.$refs.addInvoiceModal.show('create', this.invoiceList)
        },

        payNowClicked(value) {

            localStorage.setItem("redirect_to", "job_invoice")
            this.$router.push({
                path: `/checkout/invoice/${value.id}`,
                query: {
                    is_from_mobile: Number(this.$route.query.is_from_mobile) === 1 ? 1 : 0
                }
            });
        },

        editClicked(value) {
            this.$refs.addInvoiceModal.show('edit', value)
        },

        invoiceRoute(value) {
            return {
                path: `/invoice/${value.id}`,
                query: { is_from_mobile: Number(this.$route.query.is_from_mobile) === 1 ? 1 : 0 }
            };
        },

        downloadClicked(value) {
            this.$router.push(this.invoiceRoute(value));
        },

        shareClicked(invoice) {
            this.selectedInvoice = invoice;
            this.$nextTick(() => {
                this.$refs.shareInvoiceModal.show(invoice);
            });
        },

        detailClicked(invoice) {
            this.selectedInvoice = invoice;
            this.$nextTick(() => {
                this.$refs.viewInvoiceDetailModal.show();
            });
        },

        voidClicked(value) {
            this.$refs.voidModal.show(value.id)
        },

        updatePage(page) {
            this.page = page;
        },

        handleInvoiceSuccess(payload) {
            if (payload && payload.invoiceItems) {
                this.invoiceItemList = payload.invoiceItems;
            }
            this.getInvoiceList({
                job_type: 'sar',
                job_id: this.$route.params.id
            });
        },

        updateInvoiceList() {
            this.getInvoiceList({
                job_type: 'sar',
                job_id: this.$route.params.id
            })
        }
    },
}

</script>